/**
 * Pure state machine for Dynamic Input pointer fields.
 *
 * No React, no DOM — all functions are pure / referentially transparent.
 * The reducer `reduceFieldEvent` is driven by `DynamicInputOverlay` via
 * `useReducer`.  `buildCommitToken` converts the locked/typed state into
 * a coordinate-input token string that can be fed directly into
 * `parsePointToken` / `advanceDraftSession`.
 *
 * AutoCAD parity notes:
 * - Tab cycles primary → secondary → primary (wrap-around).
 * - Shift+Tab reverses direction.
 * - Locking primary field on Tab preserves the typed value; the secondary
 *   field shows the live cursor-derived value as a placeholder until the
 *   user types into it.
 * - Enter does NOT change field state — caller reads current state and
 *   then calls `buildCommitToken`, then dispatches `cancel` to reset.
 * - Esc always returns to INITIAL_FIELD_STATE.
 */

import type { WorldPoint } from "./fieldGeometry";
import { polarFromPoints } from "./fieldGeometry";
import type { DimFieldSpec } from "./draftStepFieldConfig";
// Point-filter / temporary-tracking starters (FROM/M2P/.X.Y.Z — ТЗ
// §8.6/§10.4): DI's own text buffer must recognise + pass these through
// literally (Codex PR #504 P2 — "Let dot filters reach the command line").
import { classifyPointFilterToken } from "@dxf-ai-editor/coordinate-input";

// ─── Field key ───────────────────────────────────────────────────────────────

export type FieldKey = "primary" | "secondary";

// ─── State ───────────────────────────────────────────────────────────────────

export type FieldState = {
  /** Which input currently has focus (null = no typing active). */
  focused: FieldKey | null;
  /**
   * Locked numeric values.  When the user tabs away from a field its
   * parsed value is stored here; the field renders as read-only + lock icon.
   * null = no lock (show live cursor value).
   */
  locks: { primary: number | null; secondary: number | null };
  /** Raw text currently in each buffer. */
  buffer: { primary: string; secondary: string };
  /** Whether the buffer content is syntactically invalid. */
  invalid: { primary: boolean; secondary: boolean };
  /**
   * True once the user has pressed at least one key that triggered
   * DI editing.  Controls DYNPIVIS=1 gating: fields are only rendered
   * in edit mode when typing===true.
   */
  typing: boolean;
};

export const INITIAL_FIELD_STATE: FieldState = {
  focused: null,
  locks: { primary: null, secondary: null },
  buffer: { primary: "", secondary: "" },
  invalid: { primary: false, secondary: false },
  typing: false,
};

// ─── Events ──────────────────────────────────────────────────────────────────

export type FieldEvent =
  /** User pressed a printable key that starts DI editing. `initial` is the first character. */
  | { type: "begin-typing"; initial: string }
  /** User changed the value of an input (onChange). */
  | { type: "change-buffer"; key: FieldKey; value: string }
  /** Tab — move focus forward (primary → secondary → primary). */
  | { type: "tab" }
  /** Shift+Tab — move focus backward. */
  | { type: "shift-tab" }
  /**
   * Enter — no state change; caller reads `buildCommitToken()` then
   * dispatches `cancel` to reset.  Kept for symmetry / future use.
   */
  | { type: "commit" }
  /** Esc — reset everything to initial state. */
  | { type: "cancel" };

// ─── Coords / format ─────────────────────────────────────────────────────────

export type CoordsMode = "polar" | "cartesian";
export type InputFormat = "relative" | "absolute";

// ─── Pure helpers ─────────────────────────────────────────────────────────────

/**
 * Returns true when the string is a syntactically invalid number.
 * An empty string is considered NOT invalid (shows live placeholder).
 */
function isInvalidNumber(value: string): boolean {
  if (value.trim() === "") return false;
  // AutoCAD parity: a dimension-input field also accepts full coordinate
  // expressions like `@25,10`, `@100<45`, `#200,300`. Defer validation of
  // those to commit time (parsePointToken) — while typing we only flag
  // plainly numeric entries that aren't valid numbers.
  if (/[@#,<]/.test(value)) return false;
  // Point-filter / temporary-tracking starter (`FROM`, `M2P`/`MTP`,
  // `.X`/`.Y`/`.Z`/…): `buildCommitToken`/`buildDimCommitToken` already
  // accept these and pass them through literally so they reach
  // `interceptPointFilterText` on commit, but this validator ran BEFORE
  // that passthrough existed and only recognised coordinate-expression
  // punctuation — so a valid filter starter still rendered the field
  // `data-invalid="true"` even though Enter accepted it. Exempt anything
  // `classifyPointFilterToken` recognises the same way the commit-token
  // builders already do; it rejects plain decimals like `.5` (no axis
  // letter), so ordinary numeric entry stays validated as before. (Codex
  // PR #504 P3 — "Mark filter starters as valid DI input".)
  if (classifyPointFilterToken(value.trim())) return false;
  // Allow partial values while typing: leading minus, trailing dot.
  const n = Number(value);
  return Number.isNaN(n) || !Number.isFinite(n);
}

/**
 * True when `s` looks like an AutoCAD coordinate expression rather than
 * a plain distance (any of `@`, `#`, `,`, `<`).
 */
function isCoordinateExpression(s: string): boolean {
  return /[@#,<]/.test(s);
}

/**
 * Detect which coordinate-input mode the user's buffer is in, so the UI
 * can swap the field label for AutoCAD parity — AutoCAD's Dimension
 * Input visually switches into Pointer Input mode when the user types a
 * `@` / `#` / `,` / `<` prefix.
 */
export type CoordMode =
  | "none" // plain numeric — stays as a distance/radius/rotation etc.
  | "relative-cartesian" // "@dx,dy"
  | "absolute-cartesian" // "#x,y" or bare "x,y"
  | "relative-polar" // "@d<a"
  | "absolute-polar"; // "d<a" with no prefix

export function detectCoordMode(buffer: string): CoordMode {
  const s = buffer.trim();
  if (s === "") return "none";
  if (s.startsWith("@")) {
    return s.includes("<") ? "relative-polar" : "relative-cartesian";
  }
  if (s.startsWith("#")) {
    return s.includes("<") ? "absolute-polar" : "absolute-cartesian";
  }
  if (s.includes("<")) return "absolute-polar";
  if (s.includes(",")) return "absolute-cartesian";
  return "none";
}

/**
 * Split a coord-expression buffer into two parts for two-field render.
 * Returns left part (with any @/# prefix), right part, and the separator.
 * If the separator is absent, right is empty string.
 */
export type CoordBufferSplit = {
  left: string;
  right: string;
  sep: "," | "<";
};

export function splitCoordBuffer(buffer: string, mode: CoordMode): CoordBufferSplit {
  const sep: "," | "<" = mode.endsWith("polar") ? "<" : ",";
  const idx = buffer.indexOf(sep);
  if (idx === -1) return { left: buffer, right: "", sep };
  return {
    left: buffer.slice(0, idx),
    right: buffer.slice(idx + 1),
    sep,
  };
}

/** Try to parse a buffer string to a number; return null if unsuccessful. */
function tryParse(value: string): number | null {
  if (value.trim() === "") return null;
  const n = Number(value);
  if (Number.isNaN(n) || !Number.isFinite(n)) return null;
  return n;
}

/** Flip focus key. */
function otherKey(key: FieldKey): FieldKey {
  return key === "primary" ? "secondary" : "primary";
}

// ─── Reducer ─────────────────────────────────────────────────────────────────

export function reduceFieldEvent(
  state: FieldState,
  event: FieldEvent,
): FieldState {
  switch (event.type) {
    case "begin-typing": {
      // Lock existing locks, start fresh buffers, focus primary.
      return {
        ...state,
        typing: true,
        focused: "primary",
        buffer: { primary: event.initial, secondary: "" },
        locks: { primary: null, secondary: null },
        invalid: {
          primary: isInvalidNumber(event.initial),
          secondary: false,
        },
      };
    }

    case "change-buffer": {
      const key = event.key;
      return {
        ...state,
        buffer: { ...state.buffer, [key]: event.value },
        invalid: {
          ...state.invalid,
          [key]: isInvalidNumber(event.value),
        },
      };
    }

    case "tab": {
      if (!state.typing) return state;
      const currentFocus = state.focused ?? "primary";
      const next = otherKey(currentFocus);
      // Lock the field we are leaving (if its buffer parses).
      const parsed = tryParse(state.buffer[currentFocus]);
      const newLocks = {
        ...state.locks,
        [currentFocus]: parsed,
      };
      return {
        ...state,
        focused: next,
        locks: newLocks,
      };
    }

    case "shift-tab": {
      if (!state.typing) return state;
      const currentFocus = state.focused ?? "secondary";
      const prev = otherKey(currentFocus);
      const parsed = tryParse(state.buffer[currentFocus]);
      const newLocks = {
        ...state.locks,
        [currentFocus]: parsed,
      };
      return {
        ...state,
        focused: prev,
        locks: newLocks,
      };
    }

    case "commit": {
      // No state change — caller reads state then issues cancel externally.
      return state;
    }

    case "cancel": {
      return { ...INITIAL_FIELD_STATE };
    }
  }
}

// ─── Token builder ────────────────────────────────────────────────────────────

/**
 * Builds a coordinate-input token string from the current field state.
 *
 * Returns null when there is nothing to commit (not typing, both buffers
 * empty, or the active buffer is invalid).
 *
 * Token formats (mirrors `parsePointToken` in `coordinateInput.ts`):
 *   polar + relative:     `@dist<angle`   (e.g. `@25<90`)
 *   polar + absolute:     `dist<angle`    (bare polar — not supported by
 *                          the current parser; falls back to `@dist<angle`
 *                          from the origin; documented as a known gap)
 *   cartesian + relative: `@dx,dy`        (e.g. `@10,20`)
 *   cartesian + absolute: `x,y`           (e.g. `100,200`)
 *
 * When only one field has been filled, the missing value is inferred from
 * the current cursor position relative to the reference point.
 */
export function buildCommitToken(
  state: FieldState,
  mode: CoordsMode,
  format: InputFormat,
  cursorWorld: WorldPoint,
  referenceWorld: WorldPoint | null,
): string | null {
  if (!state.typing) return null;

  const primaryBuf = state.buffer.primary;
  const secondaryBuf = state.buffer.secondary;

  // If both buffers are empty, nothing to commit.
  if (primaryBuf.trim() === "" && secondaryBuf.trim() === "") return null;

  // Point-filter / temporary-tracking starter (`FROM`, `M2P`/`MTP`,
  // `.X`/`.Y`/`.Z`/…): the underlying `<input type="text">` accepts any
  // keystroke, so a buffer CAN already hold a literal starter token — only
  // the numeric composition below was blind to it (tries to parse it as a
  // coordinate, fails, silently drops the filter). When nothing has been
  // locked yet and ONE buffer classifies as a filter starter (the other
  // still empty — e.g. a user who Tabs to Y/angle first, a normal DI flow,
  // types the starter into `secondaryBuf` instead), emit it as a literal
  // command token — identical to what typing it into the classic command
  // line and pressing Enter would produce — so it reaches
  // `interceptPointFilterText` via the normal `onCommit` → `submitCommandText`
  // path instead of being mis-parsed as a broken number.
  // `classifyPointFilterToken` rejects plain decimals like `.5` (no axis
  // letter), so ordinary numeric entry is unaffected. Mirrors the same
  // primary/secondary symmetry `buildDimCommitToken` below already applies.
  // (Codex PR #504 P3 — "Handle secondary pointer-input filter starters".)
  if (state.locks.primary === null && state.locks.secondary === null) {
    if (secondaryBuf.trim() === "" && classifyPointFilterToken(primaryBuf.trim())) {
      return primaryBuf.trim();
    }
    if (primaryBuf.trim() === "" && classifyPointFilterToken(secondaryBuf.trim())) {
      return secondaryBuf.trim();
    }
  }

  // Respect locks: use locked value if available, else buffer.
  const primaryVal =
    state.locks.primary !== null
      ? state.locks.primary
      : tryParse(primaryBuf);
  const secondaryVal =
    state.locks.secondary !== null
      ? state.locks.secondary
      : tryParse(secondaryBuf);

  if (mode === "polar") {
    // primary = distance, secondary = angle (degrees).
    let dist = primaryVal;
    let angleDeg = secondaryVal;

    // Infer missing values from cursor if we have a reference point.
    if (referenceWorld) {
      const polar = polarFromPoints(referenceWorld, cursorWorld);
      if (dist === null) {
        dist = polar.dist;
      }
      if (angleDeg === null) {
        angleDeg = polar.angleDeg;
      }
    } else {
      // No reference: fall back to 0 for missing values.
      if (dist === null) dist = 0;
      if (angleDeg === null) angleDeg = 0;
    }

    if (format === "relative") {
      return `@${dist}<${angleDeg}`;
    }
    // Absolute polar: the coordinate-input parser does NOT currently support
    // bare `dist<angle` without a reference.  We emit `@dist<angle` which is
    // relative — this is a known AutoCAD-parity gap (Phase 1 scope only).
    // Document: issue ref = DI-ABSOLUTE-POLAR, plan: parse bare polar in
    // packages/coordinate-input/src/coordinateInput.ts Phase 4.
    return `@${dist}<${angleDeg}`;
  }

  // Cartesian mode.
  let dx = primaryVal; // X (or dX in relative)
  let dy = secondaryVal; // Y (or dY in relative)

  if (format === "relative") {
    if (referenceWorld) {
      if (dx === null) dx = cursorWorld.x - referenceWorld.x;
      if (dy === null) dy = cursorWorld.y - referenceWorld.y;
    } else {
      if (dx === null) dx = 0;
      if (dy === null) dy = 0;
    }
    return `@${dx},${dy}`;
  }

  // Absolute cartesian.
  if (dx === null) dx = cursorWorld.x;
  if (dy === null) dy = cursorWorld.y;
  return `${dx},${dy}`;
}

// ─── Dimension Input token builder ────────────────────────────────────────────

/**
 * Builds a coordinate-input token for Dimension Input mode.
 *
 * Unlike Pointer Input (which always has two fields for dist+angle or x+y),
 * Dimension Input has a primary field whose *meaning* depends on the
 * DraftStep kind:
 *
 *  - "length"         → `@{dist}<{cursorAngle}` — the user-typed value is
 *                       the distance; the direction is taken from the live
 *                       cursor angle relative to the reference point.
 *  - "radius"         → raw `{value}` — advanceDraftSessionWithText treats
 *                       a bare number as the circle/arc radius.
 *  - "diameter"       → raw `{value}` — same treatment as radius.
 *  - "rotation"       → raw `{value}` — degrees entered directly.
 *  - "angle"          → raw `{value}` — subtended angle in degrees.
 *  - "scalar"         → raw `{value}` — unitless multiplier (e.g. hatch
 *                       pattern scale); NOT a polar distance token.
 *  - "width-height"   → `@{w},{h}` — cartesian relative. Secondary field is
 *                       always rendered; if buffer empty / lock null the
 *                       missing axis is inferred from cursor delta.
 *  - "distance-angle" → `@{dist}<{angle}` — relative-polar. Both fields
 *                       always rendered; missing values inferred from
 *                       cursor (primary→cursor distance, secondary→cursor
 *                       angle).
 *  - "none"           → null (should not be called for this kind).
 *
 * Returns null when:
 *  - kind is "none"
 *  - not in typing state
 *  - primary buffer is empty AND no lock on primary (nothing entered)
 *  - primary buffer is syntactically invalid
 *
 * Special: width-height and distance-angle accept commit when EITHER
 * primary or secondary has a value (the missing axis is inferred from
 * the cursor); for the other kinds primary must be present.
 */
export function buildDimCommitToken(
  state: FieldState,
  spec: DimFieldSpec,
  cursorWorld: WorldPoint,
  referenceWorld: WorldPoint | null,
  _twoFields: boolean,
): string | null {
  if (spec.kind === "none") return null;
  if (!state.typing) return null;

  const primaryBuf = state.buffer.primary.trim();
  const secondaryBuf = state.buffer.secondary.trim();

  // For two-field kinds (width-height, distance-angle) the user may have
  // typed only into the secondary field. For all others, primary must
  // have a value.
  const isTwoFieldKind =
    spec.kind === "width-height" || spec.kind === "distance-angle";
  const hasPrimaryInput =
    primaryBuf !== "" || state.locks.primary !== null;
  const hasSecondaryInput =
    secondaryBuf !== "" || state.locks.secondary !== null;
  if (!hasPrimaryInput && !(isTwoFieldKind && hasSecondaryInput)) {
    return null;
  }

  // AutoCAD parity: if the user typed a full coordinate expression
  // (`@dx,dy`, `@d<a`, `#x,y`) into the dim field, hand it straight to
  // the coordinate-input parser. This lets users enter relative /
  // polar / absolute coordinates directly — no need to switch to the
  // command line.
  if (primaryBuf !== "" && isCoordinateExpression(primaryBuf)) {
    return primaryBuf;
  }

  // Point-filter / temporary-tracking starter (`FROM`, `M2P`/`MTP`,
  // `.X`/`.Y`/`.Z`/…): Dimension Input (DYNMODE=3, the default) routes
  // Enter through THIS builder, not `buildCommitToken`, at point prompts
  // like `line-next-point` — the same literal-buffer gap applies here.
  // Without this, `.X` typed into the focused DI dim field falls through
  // to the numeric `switch` below, is treated as a missing distance, and
  // silently commits a normal cursor-derived point instead of reaching
  // `interceptPointFilterText`. (Codex PR #504 P2 — "Handle filter
  // starters in dimension input too".)
  //
  // A user who Tabs to the SECONDARY (angle) field before typing anything
  // — a normal DI flow — has the starter in `secondaryBuf`, not
  // `primaryBuf`; classify whichever single field actually holds it.
  // (Codex PR #504 P2 — "Pass secondary-field filter starters through".)
  if (state.locks.primary === null && state.locks.secondary === null) {
    if (secondaryBuf === "" && classifyPointFilterToken(primaryBuf)) {
      return primaryBuf;
    }
    if (primaryBuf === "" && classifyPointFilterToken(secondaryBuf)) {
      return secondaryBuf;
    }
  }

  // Respect locks (e.g. user tabbed to secondary and primary is locked).
  const primaryVal =
    state.locks.primary !== null
      ? state.locks.primary
      : tryParse(primaryBuf);

  switch (spec.kind) {
    case "length": {
      // AutoCAD Dimension Input: the user types a distance, the angle is
      // inferred from the live cursor direction (reference → cursor).
      if (primaryVal === null) return null;
      let angleDeg = 0;
      if (referenceWorld) {
        const polar = polarFromPoints(referenceWorld, cursorWorld);
        angleDeg = polar.angleDeg;
      }
      return `@${primaryVal}<${angleDeg}`;
    }

    case "radius":
    case "diameter":
    case "rotation":
    case "angle":
    case "scalar": {
      // Raw scalar — advanceDraftSessionWithText or the session handler
      // interprets it directly (e.g. circle radius, degrees, fill angle, or a
      // unitless multiplier such as a hatch pattern scale). NOT turned into a
      // polar distance token, so the receiving handler's parseFloat sees a
      // plain number.
      if (primaryVal === null) return null;
      return `${primaryVal}`;
    }

    case "width-height": {
      // Secondary may be a coord expression (rare — mostly used by
      // two-field rectangles), pass through if so.
      if (secondaryBuf !== "" && isCoordinateExpression(secondaryBuf)) {
        return secondaryBuf;
      }
      const secondaryVal =
        state.locks.secondary !== null
          ? state.locks.secondary
          : tryParse(secondaryBuf);

      // Infer missing axis from cursor delta.
      let width: number;
      if (primaryVal !== null) {
        width = primaryVal;
      } else if (referenceWorld) {
        width = cursorWorld.x - referenceWorld.x;
      } else {
        width = 0;
      }
      let height: number;
      if (secondaryVal !== null) {
        height = secondaryVal;
      } else if (referenceWorld) {
        height = cursorWorld.y - referenceWorld.y;
      } else {
        height = 0;
      }
      return `@${width},${height}`;
    }

    case "distance-angle": {
      // Two-field DI: primary = distance, secondary = angle (degrees).
      // Token format: `@{dist}<{angle}` — relative-polar.
      // Missing values inferred from cursor relative to reference point.
      if (secondaryBuf !== "" && isCoordinateExpression(secondaryBuf)) {
        return secondaryBuf;
      }
      const secondaryVal =
        state.locks.secondary !== null
          ? state.locks.secondary
          : tryParse(secondaryBuf);

      let dist: number;
      let angleDeg: number;
      if (referenceWorld) {
        const polar = polarFromPoints(referenceWorld, cursorWorld);
        dist = primaryVal !== null ? primaryVal : polar.dist;
        angleDeg = secondaryVal !== null ? secondaryVal : polar.angleDeg;
      } else {
        dist = primaryVal !== null ? primaryVal : 0;
        angleDeg = secondaryVal !== null ? secondaryVal : 0;
      }
      return `@${dist}<${angleDeg}`;
    }
  }
}
